import type { CollectionStatus } from './collection';

/**
 * Stored on `PreAlert`, set by the process that raises each event — never derived from date presence.
 *
 * `DriverCollected` is not in the documented contract (docs/shipment-integration-backend.md
 * spec's `ApiReceived | CustomCleared | Noa` only) — the dev API has been observed sending it
 * on at least one row. Accepted here as a temporary unblock so an out-of-spec value doesn't fail
 * the whole page; remove once the backend confirms whether it's an intentional new status or a bug.
 *
 * `PalletScanned` marks a shipment as delivered — the Delivered admin tab filters
 * `GET /api/v1/shipments` to this status (`status=PalletScanned`).
 *
 * Listed in pipeline order, the same order the backend's `sortBy=status` uses
 * (ascending) — the shipment list's status filter offers them in this order too.
 */
export const SHIPMENT_STATUSES = [
  'PreAlert',
  'ApiReceived',
  'Noa',
  'CustomCleared',
  'DriverCollected',
  'PalletScanned',
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

/** `Package.PackageStatus` — existing enum, string-serialised, unchanged by the shipment dashboard work. */
export const PACKAGE_STATUSES = [
  'AwaitingRegistration',
  'InvoiceRendered',
  'DeclaredInfo',
  'DeclaredInvoice',
  'DeclarationSubmitted',
  'DeclarationFeesReceived',
  'DeclarationReceiptReceived',
  'DeclarationClassificationReceived',
  'DeclarationFeesPaid',
  'Duplicate',
] as const;
export type PackageStatus = (typeof PACKAGE_STATUSES)[number];

/**
 * One part of a multi-NOA shipment (backend `NoticeOfArrival`) — nested under
 * a shipment detail response, ordered by `partNumber`. `pieces` is the count
 * read off this part's PDF; null when the LLM couldn't read it, in which case
 * no per-part deviation is flagged. `collectionStatus` reuses `CollectionStatus`
 * from `types/collection` — a part is either ready or collected, same as a
 * driver-collections row.
 */
export interface NoaPart {
  id: string;
  partNumber: number;
  noaDateTime: string;
  pieces: number | null;
  awbPieces: number | null;
  hasDocument: boolean;
  collectedUnits: number | null;
  collectionStatus: CollectionStatus;
}

/**
 * One row of `GET /api/v1/shipments` — `PreAlert`/AWB level, one row per AWB
 * (not one per `Package`). `status` is stored and can legitimately disagree
 * with `noaDateTime`/`customsClearedAt` — render it verbatim, never infer it.
 *
 * `status` is a plain `string`, not `ShipmentStatus` — the backend has been
 * observed sending out-of-spec values (see `SHIPMENT_STATUSES`'s
 * `DriverCollected` note), and the SPA renders whatever it receives rather
 * than validating against a known set. `ShipmentStatus` remains the type for
 * values the SPA itself sends (e.g. the `status` filter param) and the
 * known-status display maps (badge class, label).
 */
export interface ShipmentRow {
  customerName: string | null;
  subscriptionId: string;
  transportDocumentNo: string;
  manifestNo: string | null;
  flightNo: string;
  eta: string;
  noaDateTime: string | null;
  /** Sum of `pieces` across every NOA part; null only when every part's pieces are unreadable. */
  noaQuantity: number | null;
  /** Number of NOA parts ingested so far. */
  noaCount: number;
  customsClearedAt: string | null;
  /** null means not collected — render `—`, never coerce to 0. */
  driverCollected: number | null;
  /** null means not yet pallet-scanned — render `—`, never coerce to 0. */
  palletScannedOrder: number | null;
  status: string;
  totalNetWeight: number;
  totalNoOfBoxes: number;
  noOfParcels: number;
  submittedOrder: number;
}

/** One `Package` row nested under a shipment's detail response. */
export interface ShipmentPackageRow {
  orderNo: string;
  packageStatus: PackageStatus;
  totalNoOfColli: number;
  totalNetWeight: number;
  totalGrossWeight: number;
  clearanceDate: string | null;
  invoiceNo: string;
  orderDate: string | null;
}

/** The Tollit detail response: `GET /api/v1/shipments/{awb}` — the row plus etd, Packages and NOA parts. */
export interface ShipmentDetail extends ShipmentRow {
  etd: string | null;
  packages: ShipmentPackageRow[];
  noas: NoaPart[];
}

/**
 * One row of `GET /api/v1/ground-handler/shipments` — backend
 * `GroundHandlerShipmentRow`. Carries `customerName` (a handler needs to know
 * whose cargo it is holding) and `submittedOrder`, but never `subscriptionId`
 * — that is the fulfilment center's own tenant-boundary value, and a handler
 * has no customer dropdown to scope by it. Do not add it here even optionally.
 */
export interface GroundHandlerShipmentRow {
  customerName: string | null;
  transportDocumentNo: string;
  manifestNo: string | null;
  flightNo: string;
  eta: string;
  noaDateTime: string | null;
  noaQuantity: number | null;
  noaCount: number;
  customsClearedAt: string | null;
  driverCollected: number | null;
  palletScannedOrder: number | null;
  status: string;
  totalNetWeight: number;
  totalNoOfBoxes: number;
  noOfParcels: number;
  submittedOrder: number;
}

/**
 * `GET /api/v1/ground-handler/shipments/{awb}` — backend
 * `GroundHandlerShipmentDetailResponse`: the row plus `etd` and NOA parts,
 * deliberately with no `packages` — those carry the fulfilment center's own
 * commercial data (order/invoice numbers), which a ground handler has no
 * operational need for.
 */
export interface GroundHandlerShipmentDetail extends GroundHandlerShipmentRow {
  etd: string | null;
  noas: NoaPart[];
}

/** One option in `GET /api/v1/subscriptions`, backing the Customer dropdown. */
export interface SubscriptionOption {
  subscriptionId: string;
  name: string;
  /**
   * Additive fields for the invoicing customer picker (backend invoicing plan
   * §4.7). Optional so an API that predates them still parses; the picker
   * labels a customer `billingCompanyName ?? name` + ` / subCustomer`.
   */
  subCustomer?: string | null | undefined;
  billingCompanyName?: string | null | undefined;
}

/**
 * GET /api/v1/customer/shipments — the customer's own row shape (backend
 * plan D5). No customerName, no subscriptionId: this is the caller's own
 * fulfilment center, and organisationKey is deliberately never echoed to the
 * SPA (see GetMe). Do not add either field here even optionally — that is
 * the "null means all" shape the backend plan explicitly rejected for the
 * scope type, and the same reasoning applies to the response contract.
 */
export interface CustomerShipmentRow {
  transportDocumentNo: string;
  manifestNo: string | null;
  flightNo: string;
  eta: string;
  noaDateTime: string | null;
  noaQuantity: number | null;
  noaCount: number;
  customsClearedAt: string | null;
  driverCollected: number | null;
  palletScannedOrder: number | null;
  status: string;
  totalNetWeight: number;
  totalNoOfBoxes: number;
  noOfParcels: number;
  submittedOrder: number;
}

/** GET /api/v1/customer/shipments/{awb} — the row plus etd, Packages and NOA parts, scoped to the caller's own AWBs (and, within the AWB, their own Packages — backend plan T4). */
export interface CustomerShipmentDetail extends CustomerShipmentRow {
  etd: string | null;
  packages: ShipmentPackageRow[];
  noas: NoaPart[];
}
